import usePreparingScanStore from "../../../features/prepare-scan/store/usePreparingScanStore";
import useEditorStore from "../../stores/useEditorStore";
import { useRectificationStore, useElongationStore, useTrimlineStore, useThicknessStore, useFlareStore, useFlaresSmootheningStore, useMLReductionSmootheningStore, useCarveSmootheningStore } from "../../stores";
import { TOOLS_CONFIG } from "../../constants/tools";
import { clearPreModificationSocket } from '../../lib/preModificationSnapshot';

import useSliceStore from "../../../features/prepare-scan/store/useSliceStore";
import useLockingSystemStore from "../../stores/useLockingSystemStore";
import useGlobalSmoothStore from "../../stores/useGlobalSmoothStore";
import useOutwardThicknessStore from "../../stores/useOutwardThicknessStore";
import useInflateDeflateStore from "../../stores/useInflateDeflateStore";
import useRectificationSculptSmoothStore from "../../stores/useRectificationSculptSmoothStore";
import { disposePreviewObject, purgeElongationPreviews } from "../../lib/elongationPreview";

/**
 * Per-tool apply/undo wiring.
 *
 * `exec`         — build (and return) the undoable command for an apply.
 * `resetApplied` — put the tool's *own* state back to un-applied. Used only by the
 *                  snapshot fallback in `undoToolCommand`: when the live command is
 *                  gone (reload / session restore / draft load) the socket geometry
 *                  comes back from the pre-apply snapshot, and this clears the
 *                  applied flags so the card returns to its editable form.
 *
 *                  Keep these narrow — flags and disposed-preview references only.
 *                  Editing state that was never serialized (painted zones, brush
 *                  influences) cannot be resurrected here, so the tool comes back
 *                  ready to be set up again rather than half-restored.
 */
export const toolRegistry = {

};

toolRegistry[TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.TRIM.id] = {
  exec: ({ mode }) => {
    switch(mode) {
      case 'trim':
        useSliceStore.getState().executeTrimAsync();
        return null;
      case 'invert':
        useSliceStore.getState().invertSelection();
        return null;
      case 'unselect': 
        const shadedMesh = useEditorStore.getState().sceneRefs.shadedMesh;
        if(shadedMesh)
          shadedMesh.visible = true;

        usePreparingScanStore.getState().clearSelection();      
        useSliceStore.getState().clearSlicePreview();   
        return null;
    }
  },
  undo: () => {
    useEditorStore.getState().undo(); // global undo command
  }
}

toolRegistry[TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.FILL.id] = {
  exec: ({ mode }) => {
    // Correctly call the factory from the store. It returns the command object.
    return usePreparingScanStore.getState().createFillCommand();
  }
}

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_measure_reduction.id] ={
  exec: ({mode}) => {
    if( mode === 'apply'){
      const command = useRectificationStore.getState().createRectificationCommand();
      useRectificationStore.getState().setCurrentRectificationCommand(command);
      return command;
    }
    return null; // No action for other modes
  },
  resetApplied: () => {
    const s = useRectificationStore.getState();
    s.setIsRectificationApplied(false);
    s.setIsRectificationAppliedFromDraft(false);
    s.setCurrentRectificationCommand(null);
    s.setRectifiedPreview(true);
    // The reduction is no longer applied, so the socket recorded as its "before"
    // is not a before of anything.
    clearPreModificationSocket();
  },
}

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_scoop_recharge.id] = {
  exec: ({mode}) => {
    if( mode === 'apply'){
      const command = useRectificationStore.getState().createScoopRechargeCommand();
      useRectificationStore.getState().setCurrentScoopRechargeCommand(command);
      return command;
    }
    return null;
  },
  resetApplied: () => {
    const s = useRectificationStore.getState();
    s.setIsScoopRechargeApplied(false);
    s.setIsScoopRechargeAppliedFromDraft(false);
    s.setCurrentScoopRechargeCommand(null);
    // The painted zones were consumed by the apply and never serialized; null the
    // preview so it is rebuilt from the restored socket.
    s.setScoopRecharge({ zones: [], previewVisible: true, previewMesh: null });
  },
}

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_carve_smoothning.id] = {
  exec: ({ mode }) => {
    if (mode === 'apply') {
      const command = useCarveSmootheningStore.getState().createSmootheningCommand();
      useCarveSmootheningStore.getState().setCurrentSmootheningCommand(command);
      return command;
    }
    return null;
  },
  resetApplied: () => {
    const s = useCarveSmootheningStore.getState();
    s.setIsSmootheningApplied(false);
    s.setIsSmootheningAppliedFromDraft(false);
    s.setCurrentSmootheningCommand(null);
    s.setSmoothning({ zones: [], previewVisible: true, previewMesh: null });
  },
}

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_global_reduction.id] = {
  exec: ({mode}) => {
    if (mode === 'apply') {
      const command = useRectificationStore.getState().createGlobalReductionCommand();
      useRectificationStore.getState().setCurrentGlobalReductionCommand(command);
      return command;
    }
    return null;
  },
  resetApplied: () => {
    const s = useRectificationStore.getState();
    s.setIsGlobalReductionApplied(false);
    s.setIsGlobalReductionAppliedFromDraft(false);
    s.setCurrentGlobalReductionCommand(null);
    s.setGlobalReduction({ previewVisible: true, previewMesh: null, value: 0, appliedTotal: null });
  },
}

toolRegistry[TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.SMOOTH.id] = {
  exec: ({ mode }) => {
    if (mode === 'apply') {
      const command = useRectificationStore.getState().createSmootheningCommand();
      useRectificationStore.getState().setCurrentSmootheningCommand(command);
      return command;
    }
    return null;
  },
  resetApplied: () => {
    const s = useRectificationStore.getState();
    s.setIsSmootheningApplied(false);
    s.setIsSmootheningAppliedFromDraft(false);
    s.setCurrentSmootheningCommand(null);
    s.setSmoothning({ zones: [], previewVisible: true, previewMesh: null });
  },
}

/**
 * Global smoothing is offered on more than one step (Prepare Scan, Rectification,
 * Flares) with one tool id each; the store keys every applied/undo flag by that
 * id, so the entries differ only in which id they pass through.
 */
const globalSmoothEntry = (toolId) => ({
  exec: ({ mode }) => {
    if (mode === 'apply') {
      // The worker sweep already ran in the card; the command is waiting.
      const command = useGlobalSmoothStore.getState().takePendingCommand();
      if (!command) return null;
      useGlobalSmoothStore.getState().setCurrentGlobalSmoothCommand(toolId, command);
      return command;
    }
    return null;
  },
  resetApplied: () => {
    const s = useGlobalSmoothStore.getState();
    s.setIsGlobalSmoothApplied(toolId, false);
    s.setIsGlobalSmoothAppliedFromDraft(toolId, false);
    s.setCurrentGlobalSmoothCommand(toolId, null);
  },
});

toolRegistry[TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.GLOBAL_SMOOTH.id] = globalSmoothEntry(
  TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.GLOBAL_SMOOTH.id,
);

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_global_smooth.id] = globalSmoothEntry(
  TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_global_smooth.id,
);

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_global_smooth.id] =
  globalSmoothEntry(TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_global_smooth.id);

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.ELONGATION.main_elongation_elongation.id] = {
  exec: ({ mode }) => {
    if( mode === 'apply'){
      const command = useElongationStore.getState().createElongationCommand();
      useElongationStore.getState().setCurrentElongationCommand(command);
      return command;
    }
    return null;
  },
  resetApplied: () => {
    // Clearing the store reference alone would strand the preview clone in the
    // scene — nothing else owns it once the store forgets it.
    disposePreviewObject(useElongationStore.getState().previewMesh);
    purgeElongationPreviews(useEditorStore.getState().sceneRefs?.scene);
    useElongationStore.setState({
      isElongationApplied: false,
      isAppliedFromDraft: false,
      currentElongationCommand: null,
      previewMesh: null,
    });
  },
}

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.TRIMLINE.main_trimline_trimline.id] = {
  exec: ({ mode }) => {
    if( mode === 'apply'){
      const command = useTrimlineStore.getState().createTrimlineCommand();
      useTrimlineStore.getState().setCurrentTrimlineCommand(command);
      return command;
    }
    return null;
  },
  resetApplied: () => {
    const s = useTrimlineStore.getState();
    s.setIsTrimlineApplied(false);
    s.setApplied(false);
    s.setCurrentTrimlineCommand(null);
    useTrimlineStore.setState({
      isAppliedFromDraft: false,
      previewMesh: null,
      upperMesh: null,
    });
    // Drops the curve points too: they are re-derived from the landmarks (or a
    // fresh patella pick) against the socket that just came back.
    s.reset();
  },
}

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.THICKNESS.main_thickness_thickness.id] = {
  exec: ({ mode }) => {
    if( mode === 'apply'){
      const command = useThicknessStore.getState().createThicknessCommand();
      useThicknessStore.getState().setCurrentThicknessCommand(command);
      return command;
    }
    return null;
  },
  // The card drives the *outward* pipeline (this legacy hollow-shell `exec` is no
  // longer reachable from the UI), so the reset has to clear the outward store.
  resetApplied: () => {
    const s = useOutwardThicknessStore.getState();
    s.setIsApplied(false);
    s.setCurrentCommand(null);
    s.setPreviewVisible(true);
    s.setLastError(null);
    // The apply already consumed the preview; null it so it is rebuilt from the
    // socket that just came back.
    s.setPreviewMesh(null);
  },
}

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_flare.id] = {
  exec: ({ mode }) => {
    if( mode === 'apply'){
      const command = useFlareStore.getState().createFlareCommand();
      useFlareStore.getState().setCurrentFlareCommand(command);
      return command;
    }
    return null;
  },
  resetApplied: () => {
    const s = useFlareStore.getState();
    s.setFlareApplied(false);
    s.setCurrentFlareCommand(null);
    useFlareStore.setState({
      isFlareAppliedFromDraft: false,
      previewMesh: null,
      baseGeometry: null,
    });
  },
}

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_smoothning.id] = {
  exec: ({ mode }) => {
    if (mode === 'apply') {
      const command = useFlaresSmootheningStore.getState().createSmootheningCommand();
      useFlaresSmootheningStore.getState().setCurrentSmootheningCommand(command);
      return command;
    }
    return null;
  },
  resetApplied: () => {
    const s = useFlaresSmootheningStore.getState();
    s.setIsSmootheningApplied(false);
    s.setIsSmootheningAppliedFromDraft(false);
    s.setCurrentSmootheningCommand(null);
    s.setSmoothning({ zones: [], previewVisible: true, previewMesh: null });
  },
}

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.ML_REDUCTION.main_ml_reduction_smoothning.id] = {
  exec: ({ mode }) => {
    if (mode === 'apply') {
      const command = useMLReductionSmootheningStore.getState().createSmootheningCommand();
      useMLReductionSmootheningStore.getState().setCurrentSmootheningCommand(command);
      return command;
    }
    return null;
  },
  resetApplied: () => {
    const s = useMLReductionSmootheningStore.getState();
    s.setIsSmootheningApplied(false);
    s.setIsSmootheningAppliedFromDraft(false);
    s.setCurrentSmootheningCommand(null);
    s.setSmoothning({ zones: [], previewVisible: true, previewMesh: null });
  },
}

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.LOCKING_MECHANISM.main_locking_lock.id] = {
  exec: ({ mode }) => {
    if (mode === 'apply') {
      const command = useLockingSystemStore.getState().takePendingAttachmentCommand();
      if (!command) return null;
      useLockingSystemStore.getState().setCurrentAttachmentCommand(command);
      return command;
    }
    return null;
  },
  // resetBulldog clears both attachment kinds plus the panel/gizmo state, which is
  // exactly what "the attachment is gone, set it up again" needs.
  resetApplied: () => {
    const s = useLockingSystemStore.getState();
    s.resetBulldog();
    s.setPositioningPanelTab('attachment');
  },
}

/**
 * Apply/undo wiring for a sculpt brush instance.
 *
 * Every brush built on `createSculptBrushStore` commits the same way — hand the
 * sculpted working mesh to `replaceShadedMesh`, keep the original in the
 * command's closure for undo — so all of them share this entry and differ only
 * by which store it drives. One per brush *per step*, since each step has its
 * own store.
 */
const sculptBrushEntry = (store) => ({
  exec: ({ mode }) => {
    if (mode === 'apply') {
      const command = store.getState().createSculptCommand();
      if (!command) return null;
      store.getState().setCurrentCommand(command);
      return command;
    }
    return null;
  },
  resetApplied: () => {
    const s = store.getState();
    s.setIsApplied(false);
    s.setIsAppliedFromDraft(false);
    s.setCurrentCommand(null);
    // The strokes lived in the sculpt session the apply tore down and were
    // never serialized, so the tool comes back ready to sculpt again rather
    // than half-restored — same contract as the zone brushes above.
    s.setWorkingMesh(null);
    s.setSessionControls(null);
    s.setStrokeState({ strokeCount: 0, undoDepth: 0 });
  },
});

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_inflate_deflate.id] =
  sculptBrushEntry(useInflateDeflateStore);

toolRegistry[TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_sculpt_smooth.id] =
  sculptBrushEntry(useRectificationSculptSmoothStore);
